import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GenreReportRow } from '../reportsApi';
import { markGenreProgramChecked } from '../reportsApi';

const safelyGetDomain = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const formatNumber = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

export interface GenreReportTemplateProps {
  reportId: string;
  defaultKeywords: string[];
  fetcher: (keywords: string[]) => Promise<GenreReportRow[]>;
  title: string;
  subtitle: string;
  emptyMessage: string;
  emphasisNote?: string;
  allowKeywordEditing?: boolean;
  noteLabel: string;
}

const GenreReportTemplate: React.FC<GenreReportTemplateProps> = ({
  reportId,
  defaultKeywords,
  fetcher,
  title,
  subtitle,
  emptyMessage,
  emphasisNote,
  allowKeywordEditing = true,
  noteLabel
}) => {
  const [keywords, setKeywords] = React.useState<string[]>(() => defaultKeywords);
  const [pendingKeyword, setPendingKeyword] = React.useState('');

  React.useEffect(() => {
    setKeywords(defaultKeywords);
  }, [defaultKeywords]);

  const normalizedKeywords = React.useMemo(
    () => Array.from(new Set(keywords.map((keyword) => keyword.trim()).filter(Boolean))),
    [keywords]
  );

  const query = useQuery({
    queryKey: ['reports', reportId, normalizedKeywords.join('|')],
    queryFn: () => fetcher(normalizedKeywords),
    staleTime: 10 * 60 * 1000
  });
  const rows = React.useMemo(() => query.data ?? [], [query.data]);
  const queryClient = useQueryClient();
  const [pendingProgramId, setPendingProgramId] = React.useState<string | null>(null);

  const genreCheckMutation = useMutation({
    mutationFn: async ({ programId }: { programId: string }) => {
      setPendingProgramId(programId);
      await markGenreProgramChecked({ programId, reportId, label: noteLabel });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports', reportId] });
    },
    onSettled: () => {
      setPendingProgramId(null);
    }
  });

  const handleAddKeyword = React.useCallback(() => {
    const value = pendingKeyword.trim();
    if (!value) return;
    setKeywords((prev) => {
      if (prev.some((item) => item.toLowerCase() === value.toLowerCase())) return prev;
      return [...prev, value];
    });
    setPendingKeyword('');
  }, [pendingKeyword]);

  const handleRemoveKeyword = React.useCallback((value: string) => {
    setKeywords((prev) => prev.filter((item) => item !== value));
  }, []);

  const handleResetKeywords = React.useCallback(() => {
    setKeywords(defaultKeywords);
    setPendingKeyword('');
  }, [defaultKeywords]);

  const handleKeywordInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddKeyword();
    }
  };

  const canReset = React.useMemo(() => {
    const defaultsNormalized = defaultKeywords.map((keyword) => keyword.trim()).filter(Boolean).sort();
    const currentNormalized = normalizedKeywords.slice().sort();
    if (defaultsNormalized.length !== currentNormalized.length) return true;
    return defaultsNormalized.some((value, index) => value.toLowerCase() !== currentNormalized[index]?.toLowerCase());
  }, [defaultKeywords, normalizedKeywords]);

  const normalizedDefaults = React.useMemo(
    () => Array.from(new Set(defaultKeywords.map((keyword) => keyword.trim()).filter(Boolean))),
    [defaultKeywords]
  );

  const computeDisplayWins = React.useCallback((row: GenreReportRow): number | null => {
    const directWins = row.totalWinOpportunities ?? row.prizesPerCategory ?? null;
    const multiTier =
      row.hasTieredPrizes === true ||
      (row.totalWinOpportunities != null && row.totalWinOpportunities > 1) ||
      (row.prizesPerCategory != null && row.prizesPerCategory > 1);
    const fallbackWins = !multiTier && row.matchingCategoryCount > 0 ? row.matchingCategoryCount : null;
    return directWins ?? fallbackWins;
  }, []);

  const summary = React.useMemo(() => {
    if (rows.length === 0) {
      return { programs: 0, totalMatches: 0, totalWins: 0 };
    }
    return rows.reduce(
      (acc, row) => {
        acc.programs += 1;
        acc.totalMatches += row.matchingCategoryCount;
        const wins = computeDisplayWins(row);
        if (wins != null) acc.totalWins += wins;
        return acc;
      },
      { programs: 0, totalMatches: 0, totalWins: 0 }
    );
  }, [rows, computeDisplayWins]);

  const matchSourceLabels = React.useMemo(
    () => ({
      category: 'Category match',
      program_name: 'Program name match',
      organization: 'Organization match'
    } satisfies Record<string, string>),
    []
  );

  return (
    <div>
      <style>{`
        .report-intro {
          margin-bottom: 18px;
        }
        .report-intro h2 {
          margin: 0 0 4px 0;
          font-size: 22px;
          font-weight: 700;
          color: #1f2937;
        }
        .report-intro p {
          margin: 0;
          font-size: 14px;
          color: #475569;
        }
        .report-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-bottom: 16px;
        }
        .report-actions button {
          padding: 8px 16px;
          border-radius: 8px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: var(--color-surface-muted, #f8fafc);
          cursor: pointer;
          font-weight: 600;
          color: #1f2937;
          transition: background 0.15s ease, transform 0.15s ease;
        }
        .report-actions button:hover:not(:disabled) {
          background: var(--color-surface-subtle, #eef2ff);
          transform: translateY(-1px);
        }
        .report-actions button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .report-print-btn {
          background: #fde047 !important;
          color: #111827 !important;
          border: 1px solid #111827 !important;
          border-radius: 6px !important;
          padding: 8px 16px !important;
          font-weight: 700 !important;
          box-shadow: none !important;
        }
        .report-print-btn:hover:not(:disabled) {
          background: #facc15 !important;
          transform: translateY(-1px);
        }
        .keyword-panel {
          margin-bottom: 16px;
          padding: 14px 16px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 12px;
          background: #f8fafc;
        }
        .keyword-panel h3 {
          margin: 0 0 10px 0;
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
        }
        .keyword-defaults {
          margin-bottom: 8px;
          font-size: 12px;
          color: #334155;
        }
        .keyword-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 12px;
        }
        .keyword-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          border-radius: 999px;
          background: rgba(129, 140, 248, 0.18);
          color: #3730a3;
          font-size: 12px;
          font-weight: 600;
        }
        .keyword-chip button {
          border: none;
          background: transparent;
          color: inherit;
          cursor: pointer;
          font-size: 12px;
          padding: 0;
          line-height: 1;
        }
        .keyword-input-row {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          align-items: center;
        }
        .keyword-input-row input {
          flex: 1;
          min-width: 180px;
          padding: 8px 10px;
          border-radius: 8px;
          border: 1px solid rgba(15, 23, 42, 0.15);
          font-size: 14px;
        }
        .keyword-input-row button {
          padding: 8px 14px;
          border-radius: 8px;
          border: 1px solid rgba(99, 102, 241, 0.4);
          background: rgba(129, 140, 248, 0.15);
          color: #312e81;
          cursor: pointer;
          font-weight: 600;
        }
        .keyword-input-row button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 14px;
          margin-bottom: 18px;
        }
        .summary-card {
          padding: 12px 16px;
          border-radius: 12px;
          background: #f8fafc;
          border: 1px solid rgba(15, 23, 42, 0.08);
        }
        .summary-label {
          font-size: 12px;
          text-transform: uppercase;
          color: #64748b;
          margin-bottom: 4px;
          letter-spacing: 0.05em;
        }
        .summary-value {
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
        }
        table.genre-report {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
          background: #ffffff;
        }
        table.genre-report thead th {
          background: #f1f5f9;
          text-align: left;
          padding: 10px 12px;
          font-weight: 600;
          color: #0f172a;
          border-bottom: 1px solid rgba(15, 23, 42, 0.12);
        }
        table.genre-report tbody td {
          padding: 10px 12px;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          vertical-align: top;
        }
        table.genre-report tbody tr:nth-child(even) {
          background: #f8fafc;
        }
        table.genre-report tbody tr:hover {
          background: #eef2ff;
        }
        .muted {
          color: #64748b;
        }
        .tag-pill {
          display: inline-block;
          margin: 2px 4px 2px 0;
          padding: 3px 8px;
          border-radius: 0;
          background: rgba(129, 140, 248, 0.15);
          color: #3730a3;
          font-size: 11px;
          font-weight: 600;
        }
        .match-source-group {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 6px;
        }
        .match-source-badge {
          display: inline-flex;
          align-items: center;
          padding: 3px 8px;
          border-radius: 0;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.02em;
          background: rgba(226, 232, 240, 0.8);
          color: #1f2937;
        }
        .match-source-badge.program-meta {
          background: rgba(129, 140, 248, 0.2);
          color: #312e81;
        }
        .match-source-badge.category-match {
          background: rgba(45, 212, 191, 0.18);
          color: #0f766e;
        }
        .program-only-note {
          margin-top: 6px;
          font-size: 11px;
          color: #0f172a;
          background: rgba(56, 189, 248, 0.12);
          border-radius: 0;
          padding: 4px 8px;
          display: inline-block;
        }
        .checked-pill {
          display: inline-flex;
          align-items: center;
          padding: 4px 10px;
          border-radius: 0;
          background: rgba(34, 197, 94, 0.18);
          color: #166534;
          font-size: 12px;
          font-weight: 600;
        }
        .genre-check-button {
          padding: 6px 14px;
          border-radius: 8px;
          border: 1px solid rgba(59, 130, 246, 0.35);
          background: rgba(191, 219, 254, 0.4);
          color: #1d4ed8;
          cursor: pointer;
          font-weight: 600;
        }
        .genre-check-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .wins-discrepancy {
          margin-left: 6px;
          font-size: 11px;
          color: #dc2626;
          font-weight: 500;
        }
        @media print {
          .report-actions { display: none; }
          table.genre-report tbody tr:nth-child(even) { background: #ffffff; }
        }
      `}</style>

      <div className="report-intro">
        <h2>{title}</h2>
        <p>{subtitle}</p>
        {emphasisNote && (
          <p style={{ marginTop: 6, fontSize: 12, color: '#6366f1', fontWeight: 600 }}>{emphasisNote}</p>
        )}
      </div>

      {allowKeywordEditing && (
        <section className="keyword-panel" aria-label="Keyword filters">
          <h3>Search keywords</h3>
          <div className="keyword-defaults">
            Default keywords: {normalizedDefaults.length === 0 ? '—' : normalizedDefaults.join(', ')}
          </div>
          <div className="keyword-chips">
            {normalizedKeywords.length === 0 ? (
              <span className="muted">No keywords applied</span>
            ) : (
              normalizedKeywords.map((keyword) => (
                <span key={keyword} className="keyword-chip">
                  {keyword}
                  <button
                    type="button"
                    aria-label={`Remove keyword ${keyword}`}
                    onClick={() => handleRemoveKeyword(keyword)}
                  >
                    ×
                  </button>
                </span>
              ))
            )}
          </div>
          <div className="keyword-input-row">
            <input
              type="text"
              placeholder="Add keyword (e.g., faith, gospel)"
              value={pendingKeyword}
              onChange={(event) => setPendingKeyword(event.target.value)}
              onKeyDown={handleKeywordInputKeyDown}
            />
            <button type="button" onClick={handleAddKeyword} disabled={!pendingKeyword.trim()}>
              Add keyword
            </button>
            <button type="button" onClick={handleResetKeywords} disabled={!canReset}>
              Reset to defaults
            </button>
          </div>
        </section>
      )}

      <div className="report-actions">
        <button type="button" onClick={() => query.refetch()} disabled={query.isLoading}>
          Refresh data
        </button>
        <button type="button" className="report-print-btn" onClick={() => window.print()}>
          Print report
        </button>
      </div>

      {query.isError && (
        <div style={{
          padding: '12px 14px',
          borderRadius: 10,
          border: '1px solid rgba(220, 38, 38, 0.2)',
          background: 'rgba(254, 226, 226, 0.8)',
          color: '#991b1b',
          marginBottom: 16
        }}>
          Failed to load report. {(query.error as Error)?.message ?? 'Unknown error'}
        </div>
      )}

      {query.isLoading ? (
        <div style={{ padding: '24px 12px', color: '#475569' }}>Loading live report…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '20px 16px', color: '#475569', background: '#f8fafc', borderRadius: 12 }}>
          {emptyMessage}
        </div>
      ) : (
        <>
          <div className="summary-grid">
            <div className="summary-card">
              <div className="summary-label">Programs</div>
              <div className="summary-value">{formatNumber(summary.programs)}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Matching categories</div>
              <div className="summary-value">{formatNumber(summary.totalMatches)}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Winning opportunities</div>
              <div className="summary-value">{formatNumber(summary.totalWins)}</div>
            </div>
          </div>

          {genreCheckMutation.isError && (
            <div style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid rgba(220, 38, 38, 0.2)',
              background: 'rgba(254, 226, 226, 0.8)',
              color: '#991b1b'
            }}>
              Failed to record check. {genreCheckMutation.error instanceof Error ? genreCheckMutation.error.message : 'Unknown error'}
            </div>
          )}

          <table className="genre-report">
            <thead>
              <tr>
                <th>Program</th>
                <th>BACI</th>
                <th>Website</th>
                <th>Matching categories</th>
                <th>Category names</th>
                <th>Total categories</th>
                <th>Multi prize</th>
                <th>Total win opportunities*</th>
                <th>Vetting status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const multiTier =
                  row.hasTieredPrizes === true ||
                  (row.totalWinOpportunities != null && row.totalWinOpportunities > 1) ||
                  (row.prizesPerCategory != null && row.prizesPerCategory > 1);
                const displayWins = computeDisplayWins(row);
                const isSubmitting = genreCheckMutation.isPending && pendingProgramId === row.programId;
                const showWinsDiscrepancy =
                  displayWins != null &&
                  row.matchingCategoryCount != null &&
                  displayWins < row.matchingCategoryCount;

                return (
                  <tr key={row.programId}>
                    <td>
                      <div style={{ fontWeight: 600, color: '#111827' }}>{row.programName}</div>
                      {row.programOrganization && (
                        <div className="muted" style={{ fontSize: 12 }}>{row.programOrganization}</div>
                      )}
                      {row.matchSources.length > 0 && (
                        <div className="match-source-group">
                          {row.matchSources.map((source) => {
                            const label = matchSourceLabels[source as keyof typeof matchSourceLabels] ?? source;
                            const isProgramMeta = source === 'program_name' || source === 'organization';
                            const badgeClass = isProgramMeta ? 'match-source-badge program-meta' : 'match-source-badge category-match';
                            return (
                              <span key={source} className={badgeClass}>
                                {label}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {row.programOnlyMatch && (
                        <div className="program-only-note">Program metadata match — displaying full category list</div>
                      )}
                    </td>
                    <td>{row.baciProgramCode ?? '—'}</td>
                    <td>
                      {row.websiteUrl ? (
                        <a href={row.websiteUrl} target="_blank" rel="noopener noreferrer">
                          {safelyGetDomain(row.websiteUrl)}
                        </a>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>{formatNumber(row.matchingCategoryCount)}</td>
                    <td>
                      {row.matchingCategoryNames.length === 0 ? (
                        <span className="muted">—</span>
                      ) : (
                        row.matchingCategoryNames.map((name) => (
                          <span key={name} className="tag-pill">
                            {name}
                          </span>
                        ))
                      )}
                    </td>
                    <td>{formatNumber(row.categoryCount)}</td>
                    <td>{multiTier ? 'Yes' : 'No'}</td>
                    <td>
                      {displayWins == null ? (
                        <span className="muted">—</span>
                      ) : (
                        <span>
                          {formatNumber(displayWins)}
                          {showWinsDiscrepancy && (
                            <span
                              className="wins-discrepancy"
                              title="Winning opportunities recorded in fees/prizes are lower than the number of matching categories. Review prize data for completeness."
                            >
                              ({formatNumber(row.matchingCategoryCount)} categories)
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td>{row.vettingStatus ?? '—'}</td>
                    <td>
                      {row.genreChecked ? (
                        <span className="checked-pill">Checked</span>
                      ) : (
                        <button
                          type="button"
                          className="genre-check-button"
                          onClick={() => genreCheckMutation.mutate({ programId: row.programId })}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Checking…' : 'Check'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
            * Winning opportunities are sourced from recorded prize data. When that value falls below the number of matching
            categories, the category count is displayed in red for follow-up. Program metadata matches surface every category when
            keyword coverage is still being ingested.
          </div>
        </>
      )}
    </div>
  );
};

export default GenreReportTemplate;
